<script>
    import '$lib/styles/game-tokens.css';
    import ToastContainer from '$lib/components/ToastContainer.svelte';
    import { layoutStore } from '$lib/stores/layoutStore';
    import { onDestroy } from 'svelte';

    // Réinitialiser le store quand on quitte le layout
    onDestroy(() => {
        layoutStore.reset();
    });
</script>

<style>

    :global(:root) {
        --primary: #667eea;
        --background: #ffffff;
        --background-secondary: #f9fafb;
        --background-tertiary: #f9f9f9;
        --background-hover: #f3f3f3;
        --border: #d1d5db;
        --focus: #667eea;
        --text: #111827;
        --text-secondary: #718096;
        --textlight: #444;
        --hover: #e5e7eb;

        /* Radius */
        --radius: 0.5rem;
        --radius-lg: 12px;
        --radius-xl: 16px;

        /* Spacing */
        --spacing: 1rem;
        --spacing-sm: 0.5rem;
        --spacing-lg: 1.5rem;

        /* Font */
        --font: 'Nunito', sans-serif;

        /* Gradients */
        --gradient-bg: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        --gradient-primary: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        --gradient-secondary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

        /* Shadows */
        --shadow-sm: 0 2px 8px rgba(79, 70, 229, 0.2);
        --shadow-md: 0 4px 12px rgba(79, 70, 229, 0.3);
        --shadow-lg: 0 4px 12px rgba(0, 0, 0, 0.05);

        /* Transitions */
        --transition-fast: 0.2s ease;
        --transition-normal: 0.3s ease;

        /* Responsive layout */
        --page-gutter: clamp(0.75rem, 2.5vw, 2rem);
        --content-gap: clamp(0.75rem, 2vw, 1.5rem);
        --shell-standard-max-width: 832px;
    }

    :global(*) {
        font-family: 'Nunito', sans-serif;
    }

    :global(body) {
        background: var(--gradient-bg);
        transition: background 0.3s ease;
        margin: 0;
        min-width: 0;
    }

    :global(html) {
        min-width: 0;
        overflow-x: hidden;
    }

    :global(.form) {
        margin: auto;
        padding: var(--spacing);
        background: var(--background);
        border-radius: var(--radius);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        :global(.group) {
            margin-bottom: var(--spacing);
            display: flex;
            flex-direction: column;
        }
        :global(label) {
            margin-bottom: 0.5rem;
            color: var(--text);
            font-weight: 500;
        }
        :global(input, select, textarea) {
            padding: 0.75rem;
            border: 1px solid var(--border);
            border-radius: var(--radius);
            font-size: 1rem;
            color: var(--text);
            background-color: var(--background-secondary);
            transition: border-color 0.2s, box-shadow 0.2s;
        }
        :global(input:focus, select:focus, textarea:focus) {
            outline: none;
            border-color: var(--focus);
        }
    }

    :global(*::-webkit-scrollbar) {
        width: 8px;
        height: 8px;
    }
    :global(*::-webkit-scrollbar-track) {
        background: var(--background-secondary);
        border-radius: 4px;
    }
    :global(*::-webkit-scrollbar-thumb) {
        background: var(--border);
        border-radius: 4px;
    }
    :global(*::-webkit-scrollbar-thumb:hover) {
        background: var(--primary);
    }

    :global(button) {
        padding: 0.75rem 1.25rem;
        background-color: var(--primary);
        color: white;
        font-weight: 600;
        border: none;
        border-radius: var(--radius);
        cursor: pointer;
        transition: all 0.2s ease;
        :global(&.toggle) {
            background-color: var(--hover);
            color: var(--textlight);
            font-weight: bold;
            :global(&:hover) {
                background-color: var(--primary);
                color: white;
            }
            :global(&.active) {
                background-color: var(--primary);
                color: white;
                font-weight: bold;
            }
        }
        :global(&:hover) {
            opacity: 0.9;
        }
        :global(&.big) {
            padding: 16px 24px;
            font-size: 18px;
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
            :global(&:hover) {
                transform: translateY(-2px);
                box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
            }
        }
        :global(&.secondary) {
            flex: 1;
            background: var(--background);
            color: var(--primary);
            border: 2px solid var(--primary);
            padding: 12px 16px;
            font-size: 14px;
            font-weight: 500;
            border-radius: 8px;
            :global(&:hover) {
                background: var(--primary);
                color: white;
                transform: translateY(-1px);
            }
        }

    }

    :global(button:focus-visible,
        a:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible,
        [role='button']:focus-visible) {
        outline: var(--game-focus-ring-width) solid var(--game-focus-ring-color) !important;
        outline-offset: var(--game-focus-ring-offset) !important;
    }

    :global(main) {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
        padding: var(--page-gutter);
        background-color: var(--background-tertiary);
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    .app-shell {
        box-sizing: border-box;
        width: min(100%, var(--shell-standard-max-width));
        min-height: 100dvh;
        margin: 0 auto;
        padding: 0 var(--page-gutter);
        display: flex;
        flex-direction: column;
        background-color: var(--background-tertiary);
    }

    .app-shell.full-width {
        max-width: 100%;
        margin: 0;
        padding: 0;
        width: 100%;
    }

    :global(.app-shell:has(> .home-container)) {
        max-width: 100%;
        margin: 0;
        padding: 0;
        width: 100%;
    }

    .app-shell.full-width > :global(*),
    :global(.app-shell:has(> .home-container)) > :global(*) {
        max-width: 100%;
        width: 100%;
        box-sizing: border-box;
    }

    .app-shell.full-width > :global(.header),
    .app-shell.full-width > :global(footer),
    :global(.app-shell:has(> .home-container)) > :global(.header),
    :global(.app-shell:has(> .home-container)) > :global(footer) {
        width: min(100%, var(--shell-standard-max-width));
        max-width: var(--shell-standard-max-width);
        margin-inline: auto;
    }

    footer {
        margin-top: auto;
        text-align: center;
        font-size: 0.9rem;
        color: var(--text-secondary);
        opacity: 0.7;
        padding: 1rem 0;
    }
</style>

<div class:full-width={$layoutStore.fullWidth} class="app-shell">

    <slot />

    <footer>
        OpenSophia — Jeux éducatifs gratuits
    </footer>
</div>

<ToastContainer />
